import React, { useRef, useEffect, useState } from 'react';
import { Map } from 'immutable';
import { observer } from 'mobx-react';
import { DateTime } from 'src_common/utils/time/time';
import Loading from 'src/components/Loading/Loading';
import { Popup } from 'src/components/Popup/Popup';
import AuditDetails from 'src/components/Audits/AuditDetails';
import BetSearchResults from 'src/components/Audits/AuditDetails/AuditDetailsBetReferral';
import { DateTimeInput } from 'src/components/DateTimeInput/DateTimeInput';
import { PopupWrapper, PropertiesList } from './AuditList.style';
import { AuditListState, AuditListStatePropsType, AuditItemType } from './AuditList.state';
import { useAppStateContext } from 'src/AppState/AppState';

export const AuditList: React.FC<AuditListStatePropsType> = observer(
    ({ audits = Map(), id = '', total = 0, query: propsQuery, onQueryAuditsRequest }) => {
        const { routerBackoffice, customerTabState, models } = useAppStateContext();

        const [state] = useState(() => {
            return new AuditListState(routerBackoffice, customerTabState, models, {
                audits,
                id,
                total,
                query: propsQuery,
                onQueryAuditsRequest,
            });
        });

        const prevProps = useRef<AuditListStatePropsType>({
            audits,
            id,
            total,
            query: propsQuery,
            onQueryAuditsRequest,
        });
        useEffect(() => {
            const currentProps = { audits, id, total, query: propsQuery, onQueryAuditsRequest };
            if (prevProps.current !== currentProps) {
                state.updateFromProps(currentProps);
                prevProps.current = currentProps;
            }
        }, [audits, id, total, propsQuery, onQueryAuditsRequest, state]);

        const from = state.query.from;
        const to = state.query.to;

        const items = audits.get('items');

        //@ts-expect-error
        const firstItem = items?.first();
        const hasProperties = firstItem?.toJS()?.body?.properties != null;

        const currentProps = { audits, id, total, query: propsQuery, onQueryAuditsRequest };

        const modal =
            state.showModal === true && state.audit_id !== undefined && state.audit_id !== '' ? (
                <PopupWrapper>
                    <Popup
                        title='Audit details'
                        className='audit-details-modal'
                        priority={2000}
                        active={true}
                        onClose={state.closeModal}
                    >
                        <AuditDetails
                            id={id}
                            audit_id={state.audit_id}
                        />
                    </Popup>
                </PopupWrapper>
            ) : null;

        const showMoreButton = state.canShowMore(currentProps) ? (
            <button
                className='audit-list__show-more'
                onClick={() => state.showMore(currentProps)}
            >
                SHOW MORE
            </button>
        ) : null;
        const elements = items
            //@ts-expect-error
            ?.map((item: AuditItemType | undefined) => {
                if (item === undefined) return null;

                const itemData = item.toJS();
                const user = itemData.who;
                const properties = itemData.body?.properties;
                const itemId = String(item.get('id'));
                const when = item.get('when');

                return (
                    <tr
                        key={itemId}
                        data-test={`audit-row-${itemId}`}
                    >
                        <td>{DateTime.from(when)?.format('DD/MM/YYYY HH:mm') ?? ''}</td>
                        <td>{user.id}</td>
                        <td>{state.showDifferentUserNameByAuditType(user, id)}</td>
                        <td>{String(item.get('type'))}</td>
                        <td>{String(item.get('category'))}</td>
                        {properties != null && (
                            <td data-test='changed-properties-cell'>
                                <PropertiesList>
                                    {properties.map((changedValue, index) => (
                                        <li key={index}>{`${changedValue.name}: ${changedValue.value}`}</li>
                                    ))}
                                </PropertiesList>
                            </td>
                        )}
                        <td>
                            <a
                                href='#'
                                title={itemId}
                                onClick={(e) => {
                                    e.preventDefault();
                                    state.openModal(itemId);
                                }}
                            >
                                Open
                            </a>
                        </td>
                    </tr>
                );
            })
            ?.toArray()
            .filter(Boolean);

        if (audits.get('errors') !== undefined) {
            return (
                <div className='audit-details'>
                    <div className='audit-details-header'>{audits.get('state') === 'ERROR' && <h3>Error</h3>}</div>
                </div>
            );
        }

        return (
            <div className='audit-details'>
                <div className='row'>
                    <div className='col-6'>
                        <div className='audit-details-header'>
                            <h3>
                                {id.length > 0 ? id.charAt(0).toUpperCase() + id.split('_').join(' ').slice(1) : ''}
                            </h3>
                        </div>
                    </div>
                    <div className='col-2'>
                        <div className='form'>
                            <div className='element'>
                                <DateTimeInput
                                    value={from}
                                    onChange={(from) => state.changeInput('from', from, currentProps)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='col-2'>
                        <div className='form'>
                            <div className='element'>
                                <DateTimeInput
                                    value={to}
                                    onChange={(to) => state.changeInput('to', to, currentProps)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='col-2'></div>
                </div>

                {id.length > 0 && id.includes('betting_referred_bets') ? (
                    <BetSearchResults audits={audits} />
                ) : (
                    <table className='audits-list-table table__striped'>
                        <thead>
                            <tr>
                                <th>Change time</th>
                                <th>UserId</th>
                                <th>User</th>
                                <th>Type</th>
                                <th>Description</th>
                                {hasProperties && <th>Changed properties</th>}
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(elements) && elements.length > 0 ? (
                                elements
                            ) : (
                                <tr data-test='audit-row-no-results'>
                                    <td>
                                        <span>No results found, try to change time limits</span>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
                {showMoreButton}
                {modal}
                <Loading
                    message='Loading audits...'
                    active={audits.get('state') === 'IN_PROGRESS'}
                />
            </div>
        );
    }
);
